import { FileSources, ProtectedFilePayload } from "constants/sockets";
import { PuzzleGamePlugin } from "interfaces/puzzle-game-plugin";
import { VariantType, useSnackbar } from "notistack";
import React, { FC, createContext, useContext } from "react";
import { isDesktop } from "react-device-detect";
import { useIntl } from "react-intl";
import { RoomStatus, SummaryData } from "../types";
import { useAuthContext, useAuthUser } from "./GameAuthContext";
import { useSocketContext } from "./SocketContext";

export interface UserRoom {
	username: string;
	name: string;
	_id: string;
}
export interface ActiveUser {
	username: string;
}

export interface FileData {
	// File configuration
	_id?: string;
	id: string;
	name: string;
	size: number;
	parentId?: string;
	ext?: string;

	/** True if file is a directory */
	isDir?: boolean;
	/** True if directory is a root directory (Will appear in FileBrowser's sidebar) */
	isRootDir?: boolean;
	/** List of file ids in the directory */
	childrenIds?: string[];

	thumbnailUrl?: string;
	fileUrl?: string;

	/** True if file is drawable */
	drawable?: boolean;
	openable?: boolean;
	/**
	 * Puzzle game plugin, which holds embedded puzzle information.
	 * If this data exist, this means that the attachment is an embedded puzzle.
	 */
	puzzleGamePlugin?: PuzzleGamePlugin;
	protection?: { password?: string; locked: boolean };
	windowSize?: {
		width: number;
		height: number;
	};
	resizeable?: boolean;
	hScroll?: boolean;
	vScroll?: boolean;
}
// FileAction enum

export enum FileAction {
	CLICK = "click",
	DOUBLE_CLICK = "doubleClick",
}

export interface Room {
	id: string;
	roomId: string;
	name: string;
	status?: string;
	users: UserRoom[];
	userColorSettings: Array<{ userId: string; color: string }>;
	language: string;

	featureConfig: {
		forceStart: boolean;
	};
	isForceStarted: boolean;
	chatBotConfig: {
		channelName: string;
		botName: string;
	};

	desktopFiles: string[]; // list of folder id
	files: FileData[];
	mediaLinks?: {
		webLink?: String;
		twitterLink?: String;
		fbLink?: String;
		linkedIn?: String;
	};
	help?: Help[];

	organization: {
		id: string;
		logoURI: string;
		name: string;
		slug: string;
		theme: {
			color: Record<string, { main: string; light: string }>;
		};
	};
	roomTemplateId: string;

	playingTimeInMillis: number;
}

interface Help {
	attendedBy: String;
	attendedAt: Date;
	requestedBy: String;
	requestedAt: Date;
	isLive: boolean;
}

interface GameContextValue {
	room: Room | undefined;
	activeUsers: ActiveUser[];
	summary: SummaryData[];
	leaveRoom: () => void;
	connected: boolean;
}

const ctx = createContext<GameContextValue | undefined>(undefined);
const gameOverToastKey = "gameOverToast";

export const GameProvider: FC = (props) => {
	const intl = useIntl();
	const { socket, connected } = useSocketContext();
	const { user, logout } = useAuthContext();
	const [duplicateSession, setDuplicateSession] = React.useState<boolean>(false);
	const [room, setRoom] = React.useState<Room | undefined>();
	const [activeUsers, setActiveUsers] = React.useState<ActiveUser[]>([]);
	const [summary, setSummary] = React.useState<SummaryData[]>([]);
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();

	React.useEffect(() => {
		if (!socket || !user) {
			return;
		}
		socket.emit("joinRoom", { username: user.username, roomId: user.roomId });

		socket.on("roomData", (data: Room & { activeUsers: ActiveUser[] }) => {
			const { activeUsers, ...r } = data;
			setRoom(r);
			setActiveUsers(activeUsers);
		});

		socket.on("updateFileAcccess", (data: ProtectedFilePayload) => {
			if (data.fileSource !== FileSources.FILE_BROWSER) {
				return;
			}
			setRoom((prevRoom) =>
				prevRoom
					? {
							...prevRoom,
							files: prevRoom?.files.map((file) => {
								if (file.id === data.payload.id) {
									return data.payload;
								}
								return file;
							}),
					  }
					: prevRoom,
			);
		});

		socket.on("roomHelpDataChanged", (roomHelpData: Help[]) => {
			if (roomHelpData) {
				setRoom((prev) => ({
					...prev!,
					help: roomHelpData,
				}));
			}
		});

		socket.on("userJoined", (user) => {
			setActiveUsers((prev) => {
				return [...prev, user];
			});
		});

		socket.on("userLeft", ({ username }) => {
			setActiveUsers((prev) => {
				return prev.filter((usr) => usr.username !== username);
			});
		});

		socket.on("roomStatusChanged", (status: string) => {
			if (status) {
				setRoom((prev) => ({
					...prev!,
					status,
				}));
			}
		});

		socket.on("gameOverNotification", (notificationText?: string) => {
			if (!notificationText) return;
			const message = notificationText;
			const DesktopStyle = { width: message?.length > 80 ? 500 : undefined };
			const MobileStyle = { top: -15 };
			const STYLE = isDesktop ? DesktopStyle : MobileStyle;
			enqueueSnackbar(message, {
				variant: "info",
				key: gameOverToastKey,
				autoHideDuration: 120000,
				persist: true,
				style: STYLE,
			});
		});

		socket.on("gameOver", () => {
			if (user.isTestMode) {
				// If user is in test mode, close the window. No need to go to game over page.
				window.close();
				return;
			}
			closeSnackbar(gameOverToastKey);
			setRoom((prev) => ({
				...prev!,
				status: RoomStatus.DONE,
			}));
		});

		socket.on("duplicateSession", () => {
			setDuplicateSession(true);
			socket.emit("leaveRoom");
		});

		socket.on("summary", (summary: SummaryData[]) => {
			setSummary(summary);
		});

		socket.on("toastMessage", (text: string, autoHideDuration: number, persist: boolean, variant: VariantType) => {
			const DesktopStyle = { width: text?.length > 80 ? 500 : undefined };
			const MobileStyle = { top: -15 };
			const STYLE = isDesktop ? DesktopStyle : MobileStyle;
			enqueueSnackbar(text, {
				variant: variant || "info",
				autoHideDuration: autoHideDuration || 120000,
				persist,
				style: STYLE,
			});
		});

		return () => {
			socket.emit("leaveRoom");
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [socket, user]);

	const leaveRoom = React.useCallback(() => {
		socket!.emit("leaveRoom");
		logout();
	}, [socket, logout]);

	const value = React.useMemo(() => ({ room, activeUsers, leaveRoom, summary, connected }), [
		room,
		activeUsers,
		leaveRoom,
		summary,
		connected,
	]);

	if (duplicateSession) {
		return (
			<div>
				{intl.formatMessage({
					id: "AUTH.session.duplicate.text",
				})}
			</div>
		);
	}

	return <ctx.Provider value={value}>{props.children}</ctx.Provider>;
};

export function useGameContext(): GameContextValue {
	const context = useContext(ctx);
	if (!context) {
		throw new Error("useAuth must be put within GameProvider");
	}
	return context;
}

export function useGameRoom() {
	const { room } = useGameContext();

	if (!room) {
		throw new Error("Room is empty");
	}

	return room;
}

export function useUserColorSettings(userId: string) {
	const { userColorSettings } = useGameRoom();
	const userSetting = userColorSettings.find((setting) => setting.userId === userId);
	return userSetting?.color;
}

export function useUserColorSettingsByUsername(username: string) {
	const { users } = useGameRoom();
	const user = users.find((user) => user.name === username);
	const userId = user?._id;
	return useUserColorSettings(userId!);
}

export function useAuthUserColorSettings() {
	const { id } = useAuthUser();
	return useUserColorSettings(id);
}
